// @givmo/passkeys — keyboard focus follows the step of a view that changes in place inside a dialog
// (the B2d-lost view and the account reset).
//
// ⚠ A STEP CHANGES WHEN THE CONTROL THAT CHANGED IT IS GONE, and a button that showed it was working
// had already let focus go: the design system's `loading` disables it, and Chrome then leaves focus
// on the page behind the sheet, where the next step never takes it back (measured in Chromium, cut
// 1003g's review). So each step after the first focuses its own title (`tabIndex={-1}`: a screen
// reader reads where the person now is, and Tab goes on from there), unless the step's field takes
// focus itself.

import { useLayoutEffect, useRef } from 'react';

/** A ref for the current step's title, focused each time `step` changes (never on the first one). */
export function useStepTitleFocus(step: string) {
  const title = useRef<HTMLHeadingElement>(null);
  // The step last shown, not a first-render flag: StrictMode runs the effect twice on mount.
  const shown = useRef(step);
  // A layout effect: it runs before the dialog's own fallback (Base UI moves focus to the popup when
  // the focused control is removed), so the title, not the popup, keeps it.
  useLayoutEffect(() => {
    if (shown.current === step) return;
    shown.current = step;
    title.current?.focus();
  }, [step]);
  return title;
}
